import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import InstanceRow, { InstanceCard } from "./components/InstanceRow";
import { useAuth } from "./context/AuthContext";
import { useLogout } from "./hooks/useAuthApi";
import {
  useEC2Instances,
  useDeployInstance,
  useDestroyInstance,
  useDockerStatus,
  useSetIndefinite,
  useUploadConfig,
  useDeployProgress,
  useChangeOwner,
  useProfiles,
} from "./hooks/useInstancesApi";
import { API_BASE_URL } from "./config";
import "./AwsPage.css";

function AwsPage() {
  const [destroyingStack, setDestroyingStack] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs] = useState("");
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [expandedInstance, setExpandedInstance] = useState(null);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [instanceToDestroy, setInstanceToDestroy] = useState(null);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [instanceName, setInstanceName] = useState("");
  const [selectedProfile, setSelectedProfile] = useState("");
  const [deployType, setDeployType] = useState("ec2");
  const [deployError, setDeployError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadInstance, setUploadInstance] = useState(null);
  const [uploadContainer, setUploadContainer] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deployProgressMap, setDeployProgressMap] = useState({});
  const navigate = useNavigate();
  const logsContentRef = useRef(null);
  const fileInputRef = useRef(null);

  const { user, token } = useAuth();
  const logoutMutation = useLogout();
  const { data: instancesData, isLoading: loading, error: fetchError, refetch } = useEC2Instances();
  const deployMutation = useDeployInstance();
  const destroyMutation = useDestroyInstance();
  const indefiniteMutation = useSetIndefinite();
  const uploadMutation = useUploadConfig();
  const { data: dockerContainers = [], isLoading: loadingDocker } = useDockerStatus(
    expandedInstance?.publicIp
  );
  const [settingIndefinite, setSettingIndefinite] = useState(null);

  const changeOwnerMutation = useChangeOwner();
  const { data: profiles = [] } = useProfiles();

  const instances = instancesData?.instances || [];
  const isAdmin = instancesData?.isAdmin || false;
  const maxInstances = instancesData?.maxInstances || 3;
  const users = instancesData?.users || [];

  const error = fetchError?.message || null;
  const deploying = deployMutation.isPending;

  const canDeploy = instances.length < maxInstances;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (logsContentRef.current && !loadingLogs && logs) {
      logsContentRef.current.scrollTop = logsContentRef.current.scrollHeight;
    }
  }, [logs, loadingLogs]);

  useEffect(() => {
    const instancesNeedingProgress = instances.filter(
      (inst) =>
        inst.publicIp &&
        inst.publicIp !== "N/A" &&
        inst.state === "running" &&
        (!deployProgressMap[inst.publicIp] || !deployProgressMap[inst.publicIp].isComplete)
    );

    if (instancesNeedingProgress.length === 0) return;

    const fetchProgress = async () => {
      for (const inst of instancesNeedingProgress) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/v1/instance/${inst.publicIp}/deploy-progress`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const data = await response.json();
          if (data.success) {
            setDeployProgressMap((prev) => ({
              ...prev,
              [inst.publicIp]: data,
            }));
          }
        } catch (err) {
          console.error("Error fetching deploy progress:", err);
        }
      }
    };

    fetchProgress();
    const interval = setInterval(fetchProgress, 5000);
    return () => clearInterval(interval);
  }, [instances, token, deployProgressMap]);

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/");
  };

  const handleOpenDeployModal = () => {
    if (!canDeploy) return;
    setInstanceName("");
    setSelectedProfile("");
    setDeployType("ec2");
    setShowDeployModal(true);
  };

  const handleCloseDeployModal = () => {
    setShowDeployModal(false);
    setInstanceName("");
    setSelectedProfile("");
    setDeployType("ec2");
  };

  const handleDeployDemo = async () => {
    if (!canDeploy || !instanceName.trim()) return;

    setShowDeployModal(false);
    setDeployError(null);
    const fullName = `PS-Demo-${instanceName.trim()}`;

    try {
      const data = await deployMutation.mutateAsync({
        instanceName: fullName,
        profile: selectedProfile || null,
        deployType,
      });
      if (data.success) {
        const profileMsg = selectedProfile ? `\nProfile: ${selectedProfile}` : "";
        setSuccessMessage(
          `Deployment started successfully!\n\nInstance: ${fullName}\nStack: ${data.stackName}${profileMsg}`
        );
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error("Error deploying demo:", err);
      setDeployError(err.message || "Failed to deploy instance");
    } finally {
      setInstanceName("");
      setSelectedProfile("");
      setDeployType("ec2");
    }
  };

  const getTimeRemaining = (destroyAt) => {
    if (!destroyAt) return null;

    const destroyTime = new Date(destroyAt);
    const now = currentTime;
    const diffMs = destroyTime - now;

    if (diffMs <= 0) {
      return "Expired - Destroying...";
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${diffSeconds}s`;
    } else {
      return `${diffSeconds}s`;
    }
  };

  const handleShowLogs = async (instance) => {
    if (instance.publicIp === "N/A") {
      alert("Instance does not have a public IP address");
      return;
    }

    setSelectedInstance(instance);
    setShowLogsModal(true);
    setLoadingLogs(true);
    setLogs("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/instance-logs/${instance.publicIp}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        setLogs(data.logs || "No logs available");
      } else {
        setLogs(`Error: ${data.message || "Failed to fetch logs"}`);
      }
    } catch (err) {
      console.error("Error fetching logs:", err);
      setLogs(`Error: Failed to connect to backend. Make sure SSH key is configured.`);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleCloseLogsModal = () => {
    setShowLogsModal(false);
    setLogs("");
    setSelectedInstance(null);
    setSelectedContainer(null);
  };

  const handleRefreshLogs = async () => {
    setLoadingLogs(true);
    setLogs("");

    try {
      if (selectedContainer) {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/docker-logs/${expandedInstance.publicIp}/${selectedContainer}?lines=200`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json();

        if (data.success) {
          setLogs(data.logs || "No logs available");
        } else {
          setLogs(`Error: ${data.message || "Failed to fetch logs"}`);
        }
      } else if (selectedInstance) {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/instance-logs/${selectedInstance.publicIp}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json();

        if (data.success) {
          setLogs(data.logs || "No logs available");
        } else {
          setLogs(`Error: ${data.message || "Failed to fetch logs"}`);
        }
      }
    } catch (err) {
      console.error("Error refreshing logs:", err);
      setLogs(`Error: Failed to connect to backend. Make sure SSH key is configured.`);
    } finally {
      setLoadingLogs(false);
    }
  };

  const isServiceHealthy = (serviceName) => {
    if (!dockerContainers?.length) return false;
    const container = dockerContainers.find((c) => c.Service === serviceName);
    return container?.State === "running";
  };

  const getStateDisplayText = (state) => {
    const stateMap = {
      deploying: "Deploying...",
      pending: "Initializing",
      running: "Running",
      stopping: "Stopping",
      stopped: "Stopped",
      failed: "Deploy Failed",
    };
    return stateMap[state] || state;
  };

  const toggleInstanceExpand = async (instance) => {
    if (expandedInstance?.instanceId === instance.instanceId) {
      setExpandedInstance(null);
    } else {
      setExpandedInstance(instance);
    }
  };

  const handleShowContainerLogs = async (containerName) => {
    if (!expandedInstance || expandedInstance.publicIp === "N/A") {
      return;
    }

    setSelectedContainer(containerName);
    setShowLogsModal(true);
    setLoadingLogs(true);
    setLogs("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/docker-logs/${expandedInstance.publicIp}/${containerName}?lines=200`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();

      if (data.success) {
        setLogs(data.logs || "No logs available");
      } else {
        setLogs(`Error: ${data.message || "Failed to fetch logs"}`);
      }
    } catch (err) {
      console.error("Error fetching container logs:", err);
      setLogs(`Error: Failed to connect to backend.`);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleRestartContainer = async (containerName) => {
    if (!expandedInstance || expandedInstance.publicIp === "N/A") {
      return;
    }

    if (!window.confirm(`Are you sure you want to restart the "${containerName}" container?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/docker-restart/${expandedInstance.publicIp}/${containerName}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();

      if (data.success) {
        alert(`Container "${containerName}" restarted successfully!`);
      } else {
        alert(`Failed to restart container: ${data.message || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Error restarting container:", err);
      alert(`Error: Failed to restart container. ${err.message}`);
    }
  };

  const handleShutdown = (instance) => {
    if (!instance.pulumiStack) {
      alert(
        "This instance was not created by Pulumi and cannot be destroyed through this interface."
      );
      return;
    }

    setInstanceToDestroy(instance);
    setShowConfirmModal(true);
  };

  const handleSetIndefinite = async (instanceId, indefinite) => {
    setSettingIndefinite(instanceId);
    try {
      await indefiniteMutation.mutateAsync({ instanceId, indefinite });
    } catch (err) {
      console.error("Error setting indefinite:", err);
    } finally {
      setSettingIndefinite(null);
    }
  };

  const handleChangeOwner = async (instanceId, userId) => {
    try {
      await changeOwnerMutation.mutateAsync({ instanceId, userId });
    } catch (err) {
      console.error("Error changing owner:", err);
    }
  };

  const handleUploadConfig = (instance, containerName) => {
    setUploadInstance(instance);
    setUploadContainer(containerName);
    setUploadFile(null);
    setUploadError(null);
    setShowUploadModal(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith(".json")) {
        setUploadError("Please select a JSON file");
        return;
      }
      setUploadFile(file);
      setUploadError(null);
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile || !uploadInstance || !uploadContainer) return;

    setUploading(true);
    setUploadError(null);

    try {
      await uploadMutation.mutateAsync({
        publicIp: uploadInstance.publicIp,
        containerName: uploadContainer,
        file: uploadFile,
      });
      setShowUploadModal(false);
      setSuccessMessage(`Config "${uploadFile.name}" uploaded to ${uploadContainer} successfully!`);
      setShowSuccessModal(true);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const confirmDestroy = async () => {
    if (!instanceToDestroy) return;

    setShowConfirmModal(false);
    setDestroyingStack(instanceToDestroy.pulumiStack);

    try {
      const data = await destroyMutation.mutateAsync({
        stackName: instanceToDestroy.pulumiStack,
        publicIp: instanceToDestroy.publicIp,
      });
      if (data.success) {
        setSuccessMessage("Instance destroyed successfully!");
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error("Error destroying instance:", err);
    } finally {
      setDestroyingStack(null);
      setInstanceToDestroy(null);
    }
  };

  const cancelDestroy = () => {
    setShowConfirmModal(false);
    setInstanceToDestroy(null);
  };

  return (
    <div className="aws-page">
      <div className="aws-container">
        <div className="header">
          <div className="header-left">
            <img src="/Phaseshift_Favicon-01.png" alt="" className="header-logo" />
            <h1>Demo Instance Manager</h1>
            {user && (
              <span className="user-info">
                {user.firstName || user.email}
              </span>
            )}
          </div>
          <div className="header-actions">
            {isAdmin && (
              <button className="admin-btn" onClick={() => navigate("/admin")}>
                Admin
              </button>
            )}
            <button className="refresh-btn" onClick={() => refetch()} disabled={loading}>
              ↻ Refresh
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>

        {!loading && !error && (
          <div className="deploy-section">
            <button
              className="deploy-btn"
              onClick={handleOpenDeployModal}
              disabled={!canDeploy || deploying}
            >
              {deploying ? "Deploying..." : "+ Deploy Instance"}
            </button>
            <div className={`instance-limit ${canDeploy ? "" : "limit-reached"}`}>
              {instances.length} / {isAdmin ? "∞" : maxInstances} instances
              {!canDeploy && !isAdmin && <span className="limit-message"> - Maximum limit reached</span>}
            </div>
          </div>
        )}

        {deployError && (
          <div className="deploy-error">
            <span className="deploy-error-icon">⚠</span>
            <span className="deploy-error-text">{deployError}</span>
            <button className="deploy-error-close" onClick={() => setDeployError(null)}>×</button>
          </div>
        )}

        {loading && (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading EC2 instances...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            <p>⚠ {error}</p>
          </div>
        )}

        {!loading && !error && instances.length === 0 && (
          <div className="no-instances">
            <p>No running EC2 instances found</p>
          </div>
        )}

        {!loading && !error && instances.length > 0 && (
          <>
            <div className="table-container desktop-only">
              <table className="instances-table">
                <thead>
                  <tr>
                    <th>Instance ID</th>
                    <th>Name</th>
                    {isAdmin && <th>Owner</th>}
                    <th>State</th>
                    <th>IP Address</th>
                    <th>Auto-Destroy</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {instances.map((instance) => (
                    <InstanceRow
                      key={instance.instanceId}
                      instance={instance}
                      expandedInstance={expandedInstance}
                      toggleInstanceExpand={toggleInstanceExpand}
                      getStateDisplayText={getStateDisplayText}
                      getTimeRemaining={getTimeRemaining}
                      handleShowLogs={handleShowLogs}
                      handleShutdown={handleShutdown}
                      handleSetIndefinite={handleSetIndefinite}
                      handleUploadConfig={handleUploadConfig}
                      settingIndefinite={settingIndefinite}
                      destroyingStack={destroyingStack}
                      isServiceHealthy={isServiceHealthy}
                      loadingDocker={loadingDocker}
                      dockerContainers={dockerContainers}
                      handleShowContainerLogs={handleShowContainerLogs}
                      handleRestartContainer={handleRestartContainer}
                      isAdmin={isAdmin}
                      deployProgress={deployProgressMap[instance.publicIp]}
                      users={users}
                      onChangeOwner={handleChangeOwner}
                    />
                  ))}
                </tbody>
              </table>
              <div className="instance-count">Total instances: {instances.length}</div>
            </div>

            <div className="cards-container mobile-only">
              {instances.map((instance) => (
                <InstanceCard
                  key={instance.instanceId}
                  instance={instance}
                  expandedInstance={expandedInstance}
                  toggleInstanceExpand={toggleInstanceExpand}
                  getStateDisplayText={getStateDisplayText}
                  getTimeRemaining={getTimeRemaining}
                  handleShowLogs={handleShowLogs}
                  handleShutdown={handleShutdown}
                  handleSetIndefinite={handleSetIndefinite}
                  handleUploadConfig={handleUploadConfig}
                  settingIndefinite={settingIndefinite}
                  destroyingStack={destroyingStack}
                  isServiceHealthy={isServiceHealthy}
                  loadingDocker={loadingDocker}
                  dockerContainers={dockerContainers}
                  handleShowContainerLogs={handleShowContainerLogs}
                  handleRestartContainer={handleRestartContainer}
                  isAdmin={isAdmin}
                  deployProgress={deployProgressMap[instance.publicIp]}
                  users={users}
                  onChangeOwner={handleChangeOwner}
                />
              ))}
              <div className="instance-count-mobile">{instances.length} instance{instances.length !== 1 ? 's' : ''}</div>
            </div>
          </>
        )}

        {showLogsModal && (
          <div className="modal-overlay" onClick={handleCloseLogsModal} role="presentation">
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Escape") handleCloseLogsModal();
              }}
              role="dialog"
              aria-modal="true"
              tabIndex="-1"
            >
              <div className="modal-header">
                <div className="modal-header-left">
                  <h2>{selectedContainer ? "Container Logs" : "Instance Logs"}</h2>
                  <button
                    className="refresh-logs-btn"
                    onClick={handleRefreshLogs}
                    disabled={loadingLogs}
                    title="Refresh logs"
                  >
                    {"⟳"}
                  </button>
                </div>
                <button className="close-btn" onClick={handleCloseLogsModal}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                {selectedContainer ? (
                  <div className="instance-info">
                    <strong>Container:</strong> {selectedContainer}
                    <br />
                    <strong>Instance:</strong> {expandedInstance?.instanceId}
                  </div>
                ) : (
                  selectedInstance && (
                    <div className="instance-info">
                      <strong>Instance:</strong> {selectedInstance.instanceId} (
                      {selectedInstance.publicIp})
                    </div>
                  )
                )}
                {loadingLogs ? (
                  <div className="loading-logs">
                    <div className="spinner"></div>
                    <p>Fetching logs via SSH...</p>
                  </div>
                ) : (
                  <pre className="logs-content" ref={logsContentRef}>
                    {logs}
                  </pre>
                )}
              </div>
            </div>
          </div>
        )}

        {showSuccessModal && (
          <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
            <div
              className="modal-content success-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header success-modal-header">
                <h2>✅ Success</h2>
                <button className="close-btn" onClick={() => setShowSuccessModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-body success-modal-body">
                <p className="success-message">{successMessage}</p>
                <button className="success-modal-btn" onClick={() => setShowSuccessModal(false)}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {showConfirmModal && instanceToDestroy && (
          <div className="modal-overlay" onClick={cancelDestroy}>
            <div
              className="modal-content confirm-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header confirm-modal-header">
                <h2>⚠️ Confirm Shutdown</h2>
                <button className="close-btn" onClick={cancelDestroy}>
                  ×
                </button>
              </div>
              <div className="modal-body confirm-modal-body">
                <p className="confirm-message">
                  Are you sure you want to shutdown and destroy this instance?
                </p>
                <div className="instance-details">
                  <div>
                    <strong>Instance:</strong> {instanceToDestroy.instanceId}
                  </div>
                  <div>
                    <strong>Stack:</strong> {instanceToDestroy.pulumiStack}
                  </div>
                </div>
                <p className="warning-text">
                  This will remove the instance and all associated resources (security groups,
                  etc.).
                </p>
                <div className="confirm-actions">
                  <button className="cancel-btn" onClick={cancelDestroy}>
                    Cancel
                  </button>
                  <button className="destroy-btn" onClick={confirmDestroy}>
                    Destroy Instance
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDeployModal && (
          <div className="modal-overlay" onClick={handleCloseDeployModal}>
            <div
              className="modal-content deploy-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>🚀 Deploy New Instance</h2>
                <button className="close-btn" onClick={handleCloseDeployModal}>
                  ×
                </button>
              </div>
              <div className="modal-body deploy-modal-body">
                <div className="deploy-form">
                  <label htmlFor="instance-name">Instance Name</label>
                  <div className="name-input-wrapper">
                    <span className="name-prefix">PS-Demo-</span>
                    <input
                      id="instance-name"
                      type="text"
                      value={instanceName}
                      onChange={(e) => setInstanceName(e.target.value)}
                      placeholder="Enter name"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && instanceName.trim()) {
                          handleDeployDemo();
                        }
                      }}
                    />
                  </div>
                  <p className="name-preview">
                    Full name: <strong>PS-Demo-{instanceName || "..."}</strong>
                  </p>

                  <label htmlFor="instance-profile">Profile</label>
                  <select
                    id="instance-profile"
                    className="profile-select"
                    value={selectedProfile}
                    onChange={(e) => setSelectedProfile(e.target.value)}
                  >
                    <option value="">Default</option>
                    {profiles.map((p) => (
                      <option key={p.name} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                  </select>

                  {isAdmin && (
                    <label className="deploy-type-checkbox">
                      <input
                        type="checkbox"
                        checked={deployType === "ecs"}
                        onChange={(e) =>
                          setDeployType(e.target.checked ? "ecs" : "ec2")
                        }
                      />
                      Deploy as ECS Fargate (admin)
                    </label>
                  )}
                </div>
                <div className="deploy-actions">
                  <button className="cancel-btn" onClick={handleCloseDeployModal}>
                    Cancel
                  </button>
                  <button
                    className="deploy-confirm-btn"
                    onClick={handleDeployDemo}
                    disabled={!instanceName.trim()}
                  >
                    Deploy
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showUploadModal && (
          <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
            <div
              className="modal-content upload-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>📁 Upload Config</h2>
                <button className="close-btn" onClick={() => setShowUploadModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-body upload-modal-body">
                <p className="upload-target">
                  Uploading to: <strong>{uploadContainer}</strong> on {uploadInstance?.tags?.Name || uploadInstance?.instanceId}
                </p>
                <div className="upload-form">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />
                  <button
                    className="select-file-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadFile ? "Change File" : "Select JSON File"}
                  </button>
                  {uploadFile && (
                    <div className="selected-file">
                      <span className="file-icon">📄</span>
                      <span className="file-name">{uploadFile.name}</span>
                      <span className="file-size">({(uploadFile.size / 1024).toFixed(1)} KB)</span>
                    </div>
                  )}
                </div>
                {uploadError && (
                  <div className="upload-error">
                    <span>⚠</span> {uploadError}
                  </div>
                )}
                <div className="upload-actions">
                  <button className="cancel-btn" onClick={() => setShowUploadModal(false)}>
                    Cancel
                  </button>
                  <button
                    className="upload-confirm-btn"
                    onClick={handleUploadSubmit}
                    disabled={!uploadFile || uploading}
                  >
                    {uploading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AwsPage;
